import React from 'react';
import { ProductCategory, CATEGORY_OPTIONS, CategoryOption } from '../config/analysisPrompts';

interface CategorySelectorProps {
  selectedCategory: ProductCategory;
  onCategoryChange: (category: ProductCategory) => void;
  className?: string;
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  selectedCategory,
  onCategoryChange,
  className = ''
}) => {
  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center space-x-2">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
          Product Category
        </h4>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          (Optional - helps improve accuracy)
        </span>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {CATEGORY_OPTIONS.map((option: CategoryOption) => (
          <button
            key={option.value}
            onClick={() => onCategoryChange(option.value)}
            className={`
              relative flex items-center p-3 text-left rounded-lg border transition-all duration-200
              ${selectedCategory === option.value
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
              }
            `}
          >
            <div className="flex items-center space-x-3 w-full">
              <div className="text-lg flex-shrink-0">
                {option.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {option.label}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {option.description}
                </div>
              </div>
              {selectedCategory === option.value && (
                <div className="flex-shrink-0">
                  <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
      
      {selectedCategory !== ProductCategory.AUTO_DETECT && (
        <div className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 p-2 rounded-md">
          <span className="font-medium">Selected:</span> {CATEGORY_OPTIONS.find(opt => opt.value === selectedCategory)?.label}
          {' • '}Analysis will focus on category-specific features for improved accuracy.
        </div>
      )}
    </div>
  );
};

export default CategorySelector;
